'use client'

import { algoliaProductObjectId } from '@repo/algolia/config'
import { mapCollectionToElevarViewItemListData } from '@repo/elevar/mapper'
import { LocaleIdentifier } from '@repo/markets/types'
import { LOCAL_STORAGE_LIST, setInStorage } from '@repo/storage/service'
import useIdleObserver from '@repo/ui/hooks/useIdleObserver'
import { usePathname, useSearchParams } from 'next/navigation'
import { FunctionComponent } from 'react'
import { useTelemetry } from '../../providers/TelemetryProvider'
import { CollectionDto, CollectionItemsDto } from '../../types/collection.types'

interface Props {
  collectionDto: CollectionDto
  collectionItemsDto: CollectionItemsDto
  locale: LocaleIdentifier
}

const CollectionTelemetry: FunctionComponent<Props> = ({ collectionDto, collectionItemsDto, locale }) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const list = collectionDto?.collection?.urlSlug?.current
  const { trackElevarViewItemListEvent, trackAlgoliaViewObjectIDsEvent } = useTelemetry()

  // Keep track of last viewed list so we can store this in the cart line items,
  // and in turn, inform Elevar of which list cart line items were coming from.
  if (list) {
    setInStorage(LOCAL_STORAGE_LIST, list)
  }

  useIdleObserver(
    () => {
      if (collectionItemsDto.items.length > 0 && collectionItemsDto.shopifyProducts.length > 0) {
        // Elevar payload cannot be too large; a bit arbitrary here.
        const shopifyProducts = collectionItemsDto.shopifyProducts.slice(0, 30)

        trackElevarViewItemListEvent(mapCollectionToElevarViewItemListData(shopifyProducts, locale, list))

        trackAlgoliaViewObjectIDsEvent({
          eventName: 'View Collection Page',
          objectIDs: shopifyProducts.map((product) => algoliaProductObjectId(product.handle)),
        })
      }
    },
    {
      timeout: 1000, // Run after 1 sec. if browser does not support idle callbacks.
      delay: 1000, // Run action after 1 sec. to ensure dl_user_data event runs first.
    },
    // Dispatch events when pathname (e.g. '/collection/sandals') or URL filters params change.
    [pathname, searchParams],
  )

  return null
}

export default CollectionTelemetry
